/**
 * Zwakte-engine (§3.4) + combo-engine (§3.5, §13, §14).
 *
 * - Single-card / type / paar-combo BONUSSEN worden toegepast op het moment dat
 *   de kaart gespeeld wordt (`pasZwakteBijSpelen`).
 * - COUNTERS (aanval negeren) worden bij baas-resolutie geëvalueerd
 *   (`aanvalGenegeerd`).
 * - Cross-chakra-, 5-type-, volledige-balans- en MEGA-combo's worden na elke
 *   speelactie geëvalueerd (`evalueerCombos`), idempotent via toegepasteCombos.
 */
import type { Aanval, KaartType, Origin, Zwakte } from '../data/types';
import { CROSS_COMBOS, MEGA_COMBO, TYPE_COMBO_DREMPEL, VOLLEDIGE_BALANS } from '../data/combos';
import type { ComboEffect } from '../data/combos';
import type { BattleState } from './state';
import { log, rngInt } from './state';
import { baasEnergieVerlies, cleanse, heelSpeler, schadeAanBaas, verbinding } from './mutators';

function gespeeldeNamen(state: BattleState): string[] {
  return state.speler.gespeeldDezeBeurt.map((k) => k.naam);
}
function gespeeldeOrigins(state: BattleState): Set<Origin> {
  return new Set(state.speler.gespeeldDezeBeurt.map((k) => k.origin));
}
function gespeeldeTypes(state: BattleState): Set<KaartType> {
  return new Set(state.speler.gespeeldDezeBeurt.map((k) => k.type));
}

/** Past de bonus van een enkele zwakte toe (dmg/heal/buff/weaken/energie/reflect). */
function pasBonusToe(state: BattleState, z: Zwakte): void {
  if (z.bonusDmg) {
    schadeAanBaas(state, z.bonusDmg);
    log(state, `Zwakte benut: baas −${z.bonusDmg} HP.`);
  }
  if (z.bonusHeal) heelSpeler(state, z.bonusHeal);
  if (z.bonusBuff) state.speler.buffNext += z.bonusBuff;
  if (z.bonusEnergy) state.speler.energie += z.bonusEnergy;
  if (z.bonusWeaken) {
    state.baasState.weakenAmt = Math.max(state.baasState.weakenAmt, z.bonusWeaken);
    state.baasState.weakenTurns = Math.max(state.baasState.weakenTurns, 2);
  }
  if (z.bossEnergyLoss) baasEnergieVerlies(state, z.bossEnergyLoss);
  if (z.reflectBonus) {
    state.speler.reflectVolgende = true;
    state.speler.reflectBonus = Math.max(state.speler.reflectBonus, z.reflectBonus);
  }
  if (z.cleanse) cleanse(state, z.cleanse);
  if (z.resetStacks) state.baasState.stacks = 0;
  if (z.removeAtk) verwijderWillekeurigeAanval(state);
  if (z.clearsVeil) {
    state.speler.statuses = state.speler.statuses.filter((s) => s.kind !== 'veil');
  }
}

export function verwijderWillekeurigeAanval(state: BattleState): void {
  const bs = state.baasState;
  const beschikbaar = bs.baas.aanvallen.filter((a) => !bs.verwijderd.includes(a.naam));
  if (beschikbaar.length === 0) return;
  const doel = beschikbaar[rngInt(state.rng, beschikbaar.length)];
  bs.verwijderd.push(doel.naam);
  log(state, `Aanval permanent verwijderd: ${doel.naam}.`);
}

/** Aangeroepen direct nadat `kaart` aan gespeeldDezeBeurt is toegevoegd. */
export function pasZwakteBijSpelen(state: BattleState, kaartNaam: string, kaartType: KaartType): void {
  const namen = gespeeldeNamen(state);
  for (const z of state.baasState.baas.zwaktes) {
    // single-card bonus (alleen als deze kaart het is, en er bonusvelden zijn)
    if (z.card === kaartNaam && heeftBonus(z)) {
      pasBonusToe(state, z);
    }
    // type-bonus
    if (z.type && z.type === kaartType && heeftBonus(z)) {
      pasBonusToe(state, z);
    }
    // paar-combo: deze kaart maakt het paar compleet (de andere is al gespeeld)
    if (z.cards && z.cards.includes(kaartNaam)) {
      const ander = z.cards.find((c) => c !== kaartNaam)!;
      // tel hoe vaak deze kaart al voorkomt: pas toe zodra beide aanwezig zijn
      if (namen.includes(ander) && heeftBonus(z)) {
        pasBonusToe(state, z);
      }
    }
  }
}

function heeftBonus(z: Zwakte): boolean {
  return Boolean(
    z.bonusDmg || z.bonusHeal || z.bonusBuff || z.bonusEnergy || z.bonusWeaken || z.bossEnergyLoss || z.reflectBonus || z.clearsVeil || z.cleanse || z.resetStacks || z.removeAtk
  );
}

/** Wordt de aangekondigde aanval volledig genegeerd door een gespeelde counter? */
export function aanvalGenegeerd(state: BattleState, intent: Aanval): boolean {
  const namen = gespeeldeNamen(state);
  for (const z of state.baasState.baas.zwaktes) {
    if (!z.counters) continue;
    const raaktDezeAanval = z.counters === '*' || z.counters === intent.naam;
    if (!raaktDezeAanval) continue;
    if (z.card && namen.includes(z.card)) return true;
    if (z.cards && z.cards.every((c) => namen.includes(c))) return true;
    if (z.type && state.speler.gespeeldDezeBeurt.some((k) => k.type === z.type)) return true;
  }
  return false;
}

/* ------------------------------------------------------------------ */
/* Combo-engine                                                        */
/* ------------------------------------------------------------------ */

function pasComboEffectToe(state: BattleState, eff: ComboEffect, naam: string): void {
  if (eff.energy) state.speler.energie += eff.energy;
  if (eff.energyNext) state.speler.energieVolgende += eff.energyNext;
  if (eff.heal) heelSpeler(state, eff.heal);
  if (eff.dmg) schadeAanBaas(state, eff.dmg);
  if (eff.verbinding) verbinding(state, eff.verbinding);
  if (eff.cleanse) cleanse(state, eff.cleanse);
  if (eff.buffType) state.speler.buffOutput.push({ amt: eff.buffType.amt, resterend: eff.buffType.turns, type: eff.buffType.type });
  log(state, `Combo: ${naam}.`);
}

/** Evalueer alle combo's na een speelactie; ken nieuw-voldane één keer toe. */
export function evalueerCombos(state: BattleState): void {
  const origins = gespeeldeOrigins(state);
  const types = gespeeldeTypes(state);
  const namen = gespeeldeNamen(state);
  const gedaan = state.speler.toegepasteCombos;
  const markeer = (id: string) => gedaan.push(id);
  const isGedaan = (id: string) => gedaan.includes(id);

  // 5-type-combo (alleen vs. eindbaas, ≥5 van de 6 types)
  const eindbaas = state.baasState.baas.eindbaas;
  if (eindbaas && types.size >= TYPE_COMBO_DREMPEL && !isGedaan('5type')) {
    const bonus = state.baasState.baas.combo?.bossHpLoss ?? 0;
    if (bonus) {
      if (state.baasState.baas.verbindingFight) verbinding(state, bonus);
      else schadeAanBaas(state, bonus);
      log(state, `5-type-combo: ${state.baasState.baas.verbindingFight ? `+${bonus} Verbinding` : `baas −${bonus} HP`}.`);
      markeer('5type');
    }
  }

  // cross-chakra paar-combo's
  for (const cc of CROSS_COMBOS) {
    const id = `cc-${cc.paar[0]}-${cc.paar[1]}`;
    if (!isGedaan(id) && origins.has(cc.paar[0]) && origins.has(cc.paar[1])) {
      pasComboEffectToe(state, cc.effect, cc.naam);
      markeer(id);
    }
  }

  // volledige balans (alle 7 origins)
  if (!isGedaan('balans') && origins.size === 7) {
    const vb = VOLLEDIGE_BALANS;
    if (state.baasState.baas.verbindingFight) verbinding(state, vb.verbindingTheluun);
    else schadeAanBaas(state, vb.dmgNormaal);
    cleanse(state, 'all');
    log(state, 'VOLLEDIGE BALANS!');
    markeer('balans');
  }

  // MEGA combo (Theluun): 4 meesterschapskaarten in 1 beurt
  if (state.baasState.baas.verbindingFight && !isGedaan('mega')) {
    const megaNamen = MEGA_COMBO.kaartIds; // ids, niet namen — vergelijk via gespeelde ids
    const gespeeldeIds = state.speler.gespeeldDezeBeurt.map((k) => k.id);
    if (megaNamen.every((id) => gespeeldeIds.includes(id))) {
      verbinding(state, MEGA_COMBO.verbinding);
      log(state, `${MEGA_COMBO.naam}: +${MEGA_COMBO.verbinding} Verbinding!`);
      markeer('mega');
    }
  }

  void namen;
}
